/*
  Lista życzeń — magazyn danych w przeglądarce.

  DECYZJA usera z 2 września 2026: lista życzeń jest nasza, bez zewnętrznej
  aplikacji, a jej trwałość kończy się na przeglądarce danej osoby. Shopify nie
  pozwala motywowi zapisać niczego przy koncie klienta, więc lista nie przenosi
  się między urządzeniami - i tak ma być.

  Ten plik jest jedynym źródłem prawdy o zawartości listy. Wszystko inne
  (serduszka, licznik w nagłówku, podstrona listy) tylko czyta stąd dane i
  nasłuchuje zdarzenia `wishlist:change`.

  Kształt zapisu w pamięci przeglądarki:
    { v: 2, active: "<id listy>", lists: [ { id, name, items: [ { p, v, f, h, t } ] } ] }
  gdzie w pozycji: p = identyfikator produktu, v = identyfikator wariantu,
  f = uchwyt tkaniny, h = uchwyt produktu (potrzebny do pobrania karty),
  t = znacznik czasu dodania.

  TOŻSAMOŚĆ POZYCJI TO WARIANT PLUS TKANINA, a nie sam wariant. W tym sklepie
  warianty produktu to rozmiary (łóżko 140x200, 160x200…) albo układy modułów,
  a tkanina jest osobną osią z metapola `custom.split_by` - listing pokazuje ten
  sam produkt raz na tkaninę, przy jednym wariancie. Gdyby kluczem był sam
  wariant, polubienie łóżka w grafitowej zapalałoby serduszka wszystkich jego
  kolorów i lista pokazywałaby jedną pozycję zamiast czterech.
*/

(() => {
  if (window.wishlist) return;

  const KEY = "iwc_wishlist";
  const VERSION = 2;
  const DEFAULT_LIST_ID = "default";

  const defaultName =
    (window.wishlistStrings && window.wishlistStrings.defaultListName) || "Moja lista życzeń";

  /*
    Klucz pozycji. Tkanina bywa pusta (produkty bez rozbicia na kolory), wtedy
    kluczem jest sam wariant.
  */
  const keyOf = (variantId, fabric) =>
    fabric ? `${variantId}::${fabric}` : String(variantId);

  const itemKey = (item) => keyOf(item.v, item.f);

  const emptyState = () => ({
    v: VERSION,
    active: DEFAULT_LIST_ID,
    lists: [{ id: DEFAULT_LIST_ID, name: defaultName, items: [] }],
  });

  /*
    Odczyt jest obronny: pamięć przeglądarki bywa niedostępna (tryb prywatny,
    zablokowane dane witryny), a zapis mógł zostać ręcznie zepsuty. W obu
    przypadkach wracamy do pustej listy, zamiast wywracać stronę.
  */
  const read = () => {
    try {
      const raw = window.localStorage.getItem(KEY);
      if (!raw) return emptyState();
      const parsed = JSON.parse(raw);
      if (!parsed || !Array.isArray(parsed.lists) || parsed.lists.length === 0) {
        return emptyState();
      }
      parsed.v = VERSION;
      parsed.lists = parsed.lists
        .filter((list) => list && list.id)
        .map((list) => ({
          id: String(list.id),
          name: String(list.name || defaultName),
          items: Array.isArray(list.items)
            ? list.items.filter((item) => item && item.v).map((item) => ({
                p: String(item.p || ""),
                v: String(item.v),
                f: String(item.f || ""),
                h: String(item.h || ""),
                t: Number(item.t) || 0,
              }))
            : [],
        }));
      if (parsed.lists.length === 0) return emptyState();
      if (!parsed.lists.some((list) => list.id === parsed.active)) {
        parsed.active = parsed.lists[0].id;
      }
      return parsed;
    } catch (error) {
      return emptyState();
    }
  };

  let state = read();

  const write = () => {
    try {
      window.localStorage.setItem(KEY, JSON.stringify(state));
    } catch (error) {
      // Brak miejsca albo zablokowane dane witryny: lista działa do końca sesji.
    }
  };

  const announce = (detail = {}) => {
    document.dispatchEvent(
      new CustomEvent("wishlist:change", { detail: { ...detail, count: api.count() } })
    );
  };

  const activeList = () =>
    state.lists.find((list) => list.id === state.active) || state.lists[0];

  const findList = (listId) =>
    listId ? state.lists.find((list) => list.id === String(listId)) : activeList();

  const api = {
    /* ---------- czytanie ---------- */

    lists: () => state.lists.map((list) => ({ id: list.id, name: list.name, size: list.items.length })),

    activeListId: () => activeList().id,

    items: (listId) => {
      const list = findList(listId);
      // Najnowsze dodania na początku - klient szuka na liście tego, co właśnie polubił.
      return list ? [...list.items].sort((a, b) => b.t - a.t) : [];
    },

    count: (listId) => {
      if (listId) {
        const list = findList(listId);
        return list ? list.items.length : 0;
      }
      // Licznik w nagłówku pokazuje wszystko, co klient ma polubione, nie samą
      // aktywną listę.
      return state.lists.reduce((total, list) => total + list.items.length, 0);
    },

    key: keyOf,

    has: (variantId, fabric, listId) => {
      if (!variantId) return false;
      const key = keyOf(variantId, fabric);
      if (listId) {
        const list = findList(listId);
        return Boolean(list && list.items.some((item) => itemKey(item) === key));
      }
      return state.lists.some((list) => list.items.some((item) => itemKey(item) === key));
    },

    /* ---------- zmiana zawartości ---------- */

    add: ({ variantId, productId, handle, fabric } = {}, listId) => {
      if (!variantId) return false;
      const list = findList(listId);
      if (!list) return false;
      const key = keyOf(variantId, fabric);
      if (list.items.some((item) => itemKey(item) === key)) return false;
      list.items.push({
        p: String(productId || ""),
        v: String(variantId),
        f: String(fabric || ""),
        h: String(handle || ""),
        t: Date.now(),
      });
      write();
      announce({ action: "add", key, listId: list.id });
      return true;
    },

    remove: (variantId, fabric, listId) => {
      if (!variantId) return false;
      const key = keyOf(variantId, fabric);
      // Bez wskazanej listy usuwamy z każdej - inaczej serduszko zostałoby
      // zapalone przez pozycję na innej liście.
      const targets = listId ? [findList(listId)].filter(Boolean) : state.lists;
      let removed = false;
      targets.forEach((list) => {
        const before = list.items.length;
        list.items = list.items.filter((item) => itemKey(item) !== key);
        if (list.items.length !== before) removed = true;
      });
      if (!removed) return false;
      write();
      announce({ action: "remove", key, listId: listId || null });
      return true;
    },

    toggle: (item, listId) => {
      if (!item || !item.variantId) return false;
      return api.has(item.variantId, item.fabric, listId)
        ? !api.remove(item.variantId, item.fabric, listId)
        : api.add(item, listId);
    },

    move: (variantId, fabric, targetListId) => {
      if (!variantId || !targetListId) return false;
      const key = keyOf(variantId, fabric);
      const target = findList(targetListId);
      if (!target) return false;
      let moved = null;
      state.lists.forEach((list) => {
        if (list.id === target.id) return;
        const found = list.items.find((entry) => itemKey(entry) === key);
        if (!found) return;
        moved = found;
        list.items = list.items.filter((entry) => itemKey(entry) !== key);
      });
      if (!moved) return false;
      if (!target.items.some((entry) => itemKey(entry) === key)) target.items.push(moved);
      write();
      announce({ action: "move", key, listId: target.id });
      return true;
    },

    /* ---------- listy ---------- */

    createList: (name) => {
      const label = String(name || "").trim();
      if (!label) return null;
      const id = `l${Date.now()}`;
      state.lists.push({ id, name: label, items: [] });
      state.active = id;
      write();
      announce({ action: "list-create", listId: id });
      return id;
    },

    renameList: (listId, name) => {
      const list = findList(listId);
      const label = String(name || "").trim();
      if (!list || !label) return false;
      list.name = label;
      write();
      announce({ action: "list-rename", listId: list.id });
      return true;
    },

    deleteList: (listId) => {
      // Ostatnia lista zostaje - jest tożsama z samą listą życzeń, a nie z jedną
      // z wielu, więc jej usunięcie nie miałoby znaczenia dla klienta.
      if (state.lists.length < 2) return false;
      const list = findList(listId);
      if (!list) return false;
      state.lists = state.lists.filter((entry) => entry.id !== list.id);
      if (state.active === list.id) state.active = state.lists[0].id;
      write();
      announce({ action: "list-delete", listId: list.id });
      return true;
    },

    setActiveList: (listId) => {
      const list = findList(listId);
      if (!list || list.id === state.active) return false;
      state.active = list.id;
      write();
      announce({ action: "list-active", listId: list.id });
      return true;
    },

    clear: (listId) => {
      const list = findList(listId);
      if (!list || list.items.length === 0) return false;
      list.items = [];
      write();
      announce({ action: "clear", listId: list.id });
      return true;
    },
  };

  window.wishlist = api;

  /*
    Druga karta tej samej przeglądarki zmieniła listę - przeładowujemy stan i
    ogłaszamy to u siebie, żeby serduszka i licznik nie kłamały.
  */
  window.addEventListener("storage", (event) => {
    if (event.key !== KEY) return;
    state = read();
    announce({ action: "external" });
  });

  // Pierwsze ogłoszenie po wczytaniu: licznik i serduszka ustawiają się same.
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => announce({ action: "init" }));
  } else {
    announce({ action: "init" });
  }
})();
